import React, { useState } from "react";
import { TestSet } from "../../model_gen/evaluation";
import { TestSetService } from "../../services/TestSetService";
import { usePermissions } from "../../PermissionsProvider";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { 
  PlusIcon,
  XIcon,
  Loader2Icon
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

interface TestSetContextMenuProps {
  applicationId: string;
  applicationActionType: string;
  requestId: string;
  testSets: TestSet[];
  existingTestSetIds?: string[];
  onUpdate?: (requestId: string, testSetIds: string[]) => void;
  onOpen?: (isOpen: boolean) => void;
  children: React.ReactNode;
}

const TestSetContextMenu: React.FC<TestSetContextMenuProps> = ({
  applicationId,
  applicationActionType,
  requestId,
  testSets,
  existingTestSetIds = [],
  onUpdate,
  onOpen,
  children,
}) => {
  const { apiKey } = usePermissions();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newTestSetName, setNewTestSetName] = useState("");
  const [newTestSetDescription, setNewTestSetDescription] = useState("");
  const [selectedTestSet, setSelectedTestSet] = useState<TestSet | null>(null);
  const [newTestSetData, setNewTestSetData] = useState<{ name: string; description?: string } | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [isTagging, setIsTagging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTestSetSelect = (testSet: TestSet) => {
    setSelectedTestSet(testSet);
    setNewTestSetData(null);
    setIsTagging(true);
  };

  const handleNewTestSetSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!newTestSetName.trim()) return;
    
    setNewTestSetData({ 
      name: newTestSetName.trim(),
      description: newTestSetDescription.trim() 
    });
    setSelectedTestSet(null);
    setIsTagging(true);
    setIsAddingNew(false);
    setNewTestSetName("");
    setNewTestSetDescription("");
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newTag.trim() && tags.length < 5) {
      e.preventDefault();
      if (!tags.includes(newTag.trim())) {
        setTags([...tags, newTag.trim()]);
        setNewTag("");
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmitWithTags = () => {
    setIsSubmitting(true);
    const payload = {
      request_id: requestId,
      application_id: applicationId,
      action_type: applicationActionType,
      test_set: selectedTestSet || {
        name: newTestSetData?.name,
        description: newTestSetData?.description,
        application_id: applicationId,
        action_type: applicationActionType,
      },
      tags: tags
    };

    TestSetService.addRequestToTestSet(payload, null, apiKey).subscribe(
      (response: any) => {
        toast.success("Added to test set successfully");
        
        const newTestSetId = String(response.id);
        const updatedTestSetIds = [...existingTestSetIds, newTestSetId];
        const uniqueTestSetIds = Array.from(new Set(updatedTestSetIds));
        
        onUpdate?.(requestId, uniqueTestSetIds);
        setIsDropdownOpen(false);
        setIsTagging(false);
        setTags([]);
        setSelectedTestSet(null);
        setNewTestSetData(null);
        setIsSubmitting(false);
      },
      (error: any) => {
        console.error("Failed to add to test set:", error);
        toast.error("Failed to add to test set");
        setIsSubmitting(false);
      }
    );
  };

  const handleCancel = () => {
    setIsTagging(false);
    setIsAddingNew(false);
    setTags([]);
    setSelectedTestSet(null);
    setNewTestSetData(null);
    setNewTestSetName("");
    setNewTestSetDescription("");
    setNewTag("");
  };

  const renderTaggingContent = () => (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-gc-med text-foreground">
          Selected: {selectedTestSet?.name || newTestSetData?.name}
        </Label>
      </div>
      
      <div className="space-y-3">
        <Label className="text-sm font-gc-med text-foreground">Tags</Label>
        <div className="space-y-2">
          <Input
            placeholder="Add tags (press Enter to add)"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={handleAddTag}
            disabled={isSubmitting || tags.length >= 5}
            className="text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Press Enter to add tags. Maximum 5 tags allowed.
          </p>
        </div>
        
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="gap-1">
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  disabled={isSubmitting}
                  className="ml-1 hover:bg-muted-foreground/20 rounded-full p-0.5"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmitWithTags}
          disabled={isSubmitting}
          className="gap-2"
        >
          {isSubmitting && <Loader2Icon className="w-3 h-3 animate-spin" />}
          Submit
        </Button>
      </div>
    </div>
  );

  const renderNewTestSetForm = () => (
    <div className="p-4 space-y-4">
      <div className="space-y-1">
        <h4 className="text-sm font-gc-med text-foreground">New Test Set</h4>
      </div>
      
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-sm font-gc-med text-foreground">
            Name <span className="text-red-500">*</span>
          </Label>
          <Input
            placeholder="Test set name"
            value={newTestSetName}
            onChange={(e) => setNewTestSetName(e.target.value)}
            className="text-sm"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-sm font-gc-med text-foreground">Description</Label>
          <Textarea
            placeholder="Test set description"
            value={newTestSetDescription}
            onChange={(e) => setNewTestSetDescription(e.target.value)}
            className="text-sm resize-none"
            rows={2}
          />
        </div>
      </div>
      
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!newTestSetName.trim()}
          onClick={() => handleNewTestSetSubmit()}
        >
          Next
        </Button>
      </div>
    </div>
  );

  const renderMainContent = () => (
    <div className="p-4 space-y-4">
      <div className="space-y-1">
        <h4 className="text-sm font-gc-med text-foreground">Add to Test Set</h4>
        <p className="text-xs text-muted-foreground">
          Choose an existing test set or create a new one
        </p>
      </div>
      
      <Separator className="border-border" />
      
      {testSets.length > 0 ? (
        <div className="space-y-1">
          {testSets.map((testSet) => (
            <button
              key={testSet.id}
              onClick={() => {
                if (!existingTestSetIds.includes(String(testSet.id))) {
                  handleTestSetSelect(testSet);
                }
              }}
              disabled={existingTestSetIds.includes(String(testSet.id))}
              className={cn(
                "w-full text-left px-3 py-2 rounded-md text-sm font-gc-reg transition-colors",
                existingTestSetIds.includes(String(testSet.id))
                  ? "text-muted-foreground cursor-not-allowed"
                  : "text-foreground hover:bg-accent/50"
              )}
            >
              <div className="flex items-center justify-between">
                <span>{testSet.name}</span>
                {existingTestSetIds.includes(String(testSet.id)) && (
                  <span className="text-xs text-muted-foreground">(added)</span>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground py-2">
          No existing test sets
        </div>
      )}
      
      <Separator className="border-border" />
      
      <button
        onClick={() => setIsAddingNew(true)}
        className="w-full text-left px-3 py-2 rounded-md text-sm font-gc-reg text-foreground hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <PlusIcon className="w-4 h-4" />
          Create New Test Set
        </div>
      </button>
    </div>
  );

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={(open) => {
      if (!isTagging && !isAddingNew) {
        setIsDropdownOpen(open);
        onOpen?.(open);
        if (!open) {
          handleCancel();
        }
      }
    }}>
      <DropdownMenuTrigger asChild>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 p-0"
        onInteractOutside={(e) => {
          if (isTagging || isAddingNew) {
            e.preventDefault();
          }
        }}
      >
        {isTagging ? renderTaggingContent() : 
         isAddingNew ? renderNewTestSetForm() : 
         renderMainContent()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TestSetContextMenu;